import { defineStore } from 'pinia';
import { ref } from 'vue';

import { MyPositionType } from '@libs/trip-planner-interfaces';
import type { PlaceId, Location } from '@libs/maps';

import type { UserLocation } from '../../models';
import { useGeolocationStore } from '../geolocationStore';
import { useMapStore } from '../mapStore/mapStore';

import {
  getDefaultLabel,
  mapUserLocationFromLocation,
} from './userLocationStoreLogic';

export const useUserLocationStore = defineStore('UserLocationStore', () => {
  const mapStore = useMapStore();
  const geolocationStore = useGeolocationStore();
  const locationCache: Record<PlaceId, Location> = {};

  // State
  const startLocation = ref<UserLocation>({
    name: getDefaultLabel(),
  });
  const endLocation = ref<UserLocation>({
    name: getDefaultLabel(),
  });

  // Actions
  async function setUserStartLocation(placeId?: PlaceId): Promise<void> {
    if (placeId) {
      const location =
        getLocationFromCache(placeId) ??
        (await mapStore.getPointGeolocationFromPlaceId(placeId));
      if (location) {
        setLocationCache(placeId, location);
        startLocation.value = mapUserLocationFromLocation(location);
      }
    } else {
      startLocation.value.name = getDefaultLabel();
      startLocation.value.country = undefined;

      if (geolocationStore.userGeolocation) {
        startLocation.value.coordinates = {
          latitude: geolocationStore.userGeolocation.latitude,
          longitude: geolocationStore.userGeolocation.longitude,
        };
      }
    }

    if (startLocation.value.coordinates) {
      mapStore.setPointLocationFromPosition(
        {
          latitude: startLocation.value.coordinates.latitude,
          longitude: startLocation.value.coordinates.longitude,
        },
        MyPositionType.Start
      );
    }
  }

  async function setUserEndLocation(placeId?: PlaceId): Promise<void> {
    if (placeId) {
      const location =
        getLocationFromCache(placeId) ??
        (await mapStore.getPointGeolocationFromPlaceId(placeId));
      if (location) {
        setLocationCache(placeId, location);
        endLocation.value = mapUserLocationFromLocation(location);
      }
    } else {
      endLocation.value.name = getDefaultLabel();
      endLocation.value.country = undefined;

      if (geolocationStore.userGeolocation) {
        endLocation.value.coordinates = {
          latitude: geolocationStore.userGeolocation.latitude,
          longitude: geolocationStore.userGeolocation.longitude,
        };
      }
    }

    if (endLocation.value.coordinates) {
      mapStore.setPointLocationFromPosition(
        {
          latitude: endLocation.value.coordinates.latitude,
          longitude: endLocation.value.coordinates.longitude,
        },
        MyPositionType.End
      );
    }
  }

  function getLocationFromCache(placeId: PlaceId): Location | undefined {
    return locationCache[placeId];
  }

  function setLocationCache(placeId: PlaceId, location: Location): void {
    locationCache[placeId] = location;
  }

  return {
    // State
    startLocation,
    endLocation,
    // Actions
    setUserStartLocation,
    setUserEndLocation,
  };
});
